import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDate } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Search, ChevronDown, ChevronRight, Download, Filter, Shield } from "lucide-react";

// Preset chips for common admin audit surfaces — clicking one prefixes the
// action filter so "superadmin.force_activate" and "superadmin.deactivate"
// both show up under a single "Superadmin overrides" tab.
const AUDIT_CHIPS = [
  { key: "",             label: "All events",           color: "border-navy/30 text-navy" },
  { key: "superadmin.",  label: "Superadmin overrides", color: "border-gold text-gold-deep bg-gold/5" },
  { key: "placement.",   label: "Placement moves",      color: "border-royal/30 text-royal" },
  { key: "user.",        label: "User actions",         color: "border-olive/40 text-olive" },
  { key: "payout.",      label: "Payouts",              color: "border-rose/40 text-rose" },
];

/**
 * Audit-log viewer — filter every recorded event by action, target user, date.
 * Each row expands to reveal the full JSON meta payload for that event.
 * Route: /admin/audit-log
 */
export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("");
  const [target, setTarget] = useState("");
  const [actor, setActor] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: "300" });
    if (action) p.append("action", action);
    if (target) p.append("target", target.toUpperCase());
    if (actor)  p.append("actor",  actor.toUpperCase());
    if (from)   p.append("from_date", from);
    if (to)     p.append("to_date", to);
    return p.toString();
  }, [action, target, actor, from, to]);

  useEffect(() => {
    let alive = true;
    api.get("/admin/audit-log/actions")
      .then((r) => { if (alive) setActions(r.data.actions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/audit-log?${query}`)
      .then((r) => { if (alive) setRows(r.data.items || []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [query]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const clear = () => {
    setAction(""); setTarget(""); setActor(""); setFrom(""); setTo("");
  };

  const exportCsv = () => {
    const header = ["When", "Actor", "Actor Role", "Action", "Target", "Meta"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(escape).join(",")];
    for (const r of rows) {
      lines.push([
        r.created_at, r.actor_user_code, r.actor_role, r.action,
        r.target_user_code || "", JSON.stringify(r.meta || {}),
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <DashboardLayout
      title="Audit Log"
      subtitle={`${rows.length} event${rows.length === 1 ? "" : "s"} · every admin action is recorded`}
      actions={
        <button
          type="button"
          onClick={exportCsv}
          disabled={rows.length === 0}
          data-testid="audit-export-csv"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-olive/40 text-olive hover:bg-olive/5 rounded-sm disabled:opacity-40"
        >
          <Download size={12} /> Export CSV
        </button>
      }
    >
      {/* Preset chips — one-click filter into common audit surfaces */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="audit-chips">
        {AUDIT_CHIPS.map((c) => {
          const active = action === c.key;
          return (
            <button
              key={c.key || "all"}
              type="button"
              onClick={() => setAction(c.key)}
              data-testid={`audit-chip-${c.key || "all"}`}
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition ${
                active
                  ? "bg-navy text-gold border-navy shadow-sm"
                  : `${c.color} hover:bg-cream/60`
              }`}
            >
              {c.key === "superadmin." && <Shield size={11} />}
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-border p-4 rounded-sm mb-4 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="audit-filters">
        <div className="md:col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">Action</label>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            data-testid="audit-filter-action"
            className="w-full border border-border rounded-sm px-3 py-2 text-sm focus:border-royal outline-none"
          >
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">Target user</label>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value.toUpperCase())}
              placeholder="SM100005"
              data-testid="audit-filter-target"
              className="w-full border border-border rounded-sm pl-7 pr-2 py-2 text-sm font-mono focus:border-royal outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">Actor (admin)</label>
          <input
            type="text"
            value={actor}
            onChange={(e) => setActor(e.target.value.toUpperCase())}
            placeholder="SM100001"
            data-testid="audit-filter-actor"
            className="w-full border border-border rounded-sm px-3 py-2 text-sm font-mono focus:border-royal outline-none"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="audit-filter-from"
            className="w-full border border-border rounded-sm px-3 py-2 text-sm focus:border-royal outline-none"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="audit-filter-to"
            className="w-full border border-border rounded-sm px-3 py-2 text-sm focus:border-royal outline-none"
          />
        </div>
        <div className="md:col-span-6 flex items-center justify-between">
          <div className="text-[11px] text-navy/50 inline-flex items-center gap-1.5">
            <Filter size={11} /> {loading ? "Loading…" : `${rows.length} matched`}
          </div>
          <button
            type="button"
            onClick={clear}
            data-testid="audit-filter-clear"
            className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-border text-navy/60 hover:border-royal hover:text-royal rounded-sm"
          >
            Clear filters
          </button>
        </div>
      </div>

      {/* Rows */}
      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-navy text-white">
            <tr>
              {["", "When", "Actor", "Action", "Target"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="p-8 text-center text-navy/50">No audit events match these filters.</td></tr>
            )}
            {rows.map((r) => {
              const id = r.id || `${r.action}-${r.created_at}`;
              const isOpen = !!expanded[id];
              return (
                <>
                  <tr
                    key={id}
                    data-testid={`audit-row-${id}`}
                    onClick={() => toggle(id)}
                    className="border-b border-border last:border-0 hover:bg-cream/40 cursor-pointer"
                  >
                    <td className="px-3 py-3 text-navy/40 w-6">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="px-4 py-3 text-navy/50 text-xs whitespace-nowrap">{formatDate(r.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-navy">{r.actor_user_code}</div>
                      <div className="text-[10px] text-navy/40 capitalize">{r.actor_role || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-royal/10 text-royal border border-royal/20">
                        {r.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.target_user_code ? (
                        <Link
                          to={`/admin/members/${r.target_user_code}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-mono text-xs text-royal hover:underline"
                          data-testid={`audit-target-${id}`}
                        >
                          {r.target_user_code}
                        </Link>
                      ) : (
                        <span className="text-navy/40 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-cream/30" data-testid={`audit-meta-${id}`}>
                      <td colSpan={5} className="px-6 py-4">
                        <pre className="text-[11px] leading-relaxed text-navy/80 whitespace-pre-wrap font-mono max-h-72 overflow-y-auto bg-white border border-line rounded-sm p-3">
{JSON.stringify(r.meta || {}, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
